import {
  Button,
  Checkbox,
  Divider,
  Empty,
  Flex,
  List,
  Modal,
  Skeleton,
  Space,
  Spin,
  Typography,
  message,
  theme,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useState } from 'react';

import ProductImage from '../../../../components/productImage';
import { formatCurrency } from '../../../../utils/format';
import { AddOrderReturnContext } from '../addOrderReturnContext';

const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 56;

/** Зураг хавтгай харагдахгүйн тулд өгөх зөөлөн сүүдэр */
const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Анхны ачаалалтад харуулах хийсвэр мөрийн тоо */
const SKELETON_ROWS = 4;

/** Нэг барааны мөр — дарахад сонгогдоно, hover/сонгогдсон төлөв нь дэвсгэрээр */
const ProductRow = ({ product, checked, disabled, onToggle, token }) => {
  const [hovered, setHovered] = useState(false);

  const background = checked
    ? token.colorPrimaryBg
    : hovered && !disabled
      ? token.colorFillTertiary
      : undefined;

  return (
    <List.Item
      onClick={disabled ? undefined : () => onToggle(!checked)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '10px 12px',
        marginBottom: 4,
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        background,
        boxShadow: checked ? `inset 0 0 0 1px ${token.colorPrimaryBorder}` : undefined,
        transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
      }}
    >
      <Flex align="center" gap={12} style={{ width: '100%' }}>
        <ProductImage
          src={product?.productImage}
          alt={product?.productTitle ?? 'Барааны зураг'}
          size={IMAGE_SIZE}
          style={{
            objectFit: 'contain',
            borderRadius: 8,
            border: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorBgContainer,
            boxShadow: IMAGE_SHADOW,
          }}
        />

        <Flex vertical gap={2} flex={1} style={{ minWidth: 0 }}>
          <Text strong ellipsis={{ tooltip: product?.productTitle }}>
            {product?.productTitle ?? '-'}
          </Text>
          <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
            {product?.productSKU ?? '-'}
          </Text>
        </Flex>

        <Flex
          vertical
          align="flex-end"
          gap={2}
          style={{ minWidth: 120, fontSize: 12, whiteSpace: 'nowrap' }}
        >
          <Text strong>{formatCurrency(Number(product?.productPrice) || 0)}</Text>
          {product?.totalUnit != null && (
            <Text type="secondary">Хүргэсэн: {product.totalUnit}ш</Text>
          )}
        </Flex>

        {/* Мөр дээр дарахтай давхцаж, сонголтыг буцааж болзошгүй тул зогсооно */}
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={checked}
            disabled={disabled}
            onChange={(e) => onToggle(e.target.checked)}
          />
        </span>
      </Flex>
    </List.Item>
  );
};

const ModalSelectProduct = ({ visible = false, productType, onCancel = () => undefined }) => {
  const [products, setProducts] = useState([]);
  const [productsChange, setProductsChange] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const context = useContext(AddOrderReturnContext);
  const { token } = theme.useToken();

  const selectedProducts = context.products ?? [];

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post('/api/v1/request/products-return', {
        code: context?.selectedOrder?.code ?? '',
      });

      if (data?.statusCode !== 200) return;

      setProducts(
        (data?.result ?? []).map((item) => ({
          ...item,
          totalUnitReturn: 0,
          totalPriceReturn: 0,
        }))
      );

      setProductsChange(
        (data?.productsChange ?? []).map((item) => ({
          _id: item?._id,
          baseSku: item?.baseSku,
          productImage: item?.imageCover,
          productPrice: item?.price,
          productSKU: item?.productSku,
          productTitle: item?.title,
          totalUnitReturn: 0,
          totalPriceReturn: 0,
          totalVolume: 1,
          totalVolumeV: 1,
          totalWeight: 1,
        }))
      );
    } catch (error) {
      console.error('fetchProducts: ', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    //eslint-disable-next-line
  }, []);

  const rows = productType === 'productChange' ? productsChange : products;

  /** Аль хэдийн нэмэгдсэн барааг дахин сонгуулахгүй */
  const addedIds = selectedProducts.map((item) => item._id);
  const selectableRows = rows.filter((item) => !addedIds.includes(item._id));
  const allChecked = selectableRows.length > 0 && checkedIds.length === selectableRows.length;

  const onToggle = (id, checked) =>
    setCheckedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));

  const onCheckAll = (checked) =>
    setCheckedIds(checked ? selectableRows.map((item) => item._id) : []);

  const onOk = () => {
    if (checkedIds.length === 0) {
      message.error('Та бараагаа сонгож оруулна уу.');
      return;
    }

    const appendProduct = rows
      .filter((item) => checkedIds.includes(item._id))
      .map((item) => ({ ...item, productType }));

    context.addProduct(appendProduct);
    onCancel();
    // Урт текст тул уншиж амжихаар арай удаан харуулна
    message.success(
      'Бараа амжилттай нэмэгдлээ. Бусад бараагаа "Бараа нэмэх" дээр дарж оруулна уу.',
      5
    );
  };

  const renderBody = () => {
    if (loading && rows.length === 0) {
      return (
        <div style={{ padding: '16px 12px 0' }}>
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton
              key={index}
              active
              avatar={{ shape: 'square', size: IMAGE_SIZE }}
              title={false}
              paragraph={{ rows: 2, width: ['60%', '35%'] }}
              style={{ marginBottom: 20 }}
            />
          ))}
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Буцаах бараа олдсонгүй."
          style={{ padding: '48px 0' }}
        />
      );
    }

    return (
      <Spin spinning={loading}>
        <List
          // Мөр бүр өөрийн radius/дэвсгэртэй тул хуваах зураас илүүц
          split={false}
          dataSource={rows}
          renderItem={(product) => (
            <ProductRow
              key={product?._id}
              product={product}
              checked={checkedIds.includes(product?._id)}
              disabled={addedIds.includes(product?._id)}
              onToggle={(checked) => onToggle(product?._id, checked)}
              token={token}
            />
          )}
        />
      </Spin>
    );
  };

  return (
    <Modal
      title="Бараа сонгох"
      open={visible}
      onOk={onOk}
      onCancel={onCancel}
      width={640}
      centered
      footer={
        <Flex align="center" justify="space-between" gap={16}>
          {/* Сонголт хийгээгүй үед зай нь хэвээр үлдэж товчнууд үсрэхгүй */}
          <Text type="secondary">
            {checkedIds.length > 0 ? `${checkedIds.length} бараа сонгогдсон` : ''}
          </Text>
          <Space>
            <Button onClick={onCancel}>Буцах</Button>
            <Button type="primary" onClick={onOk} disabled={checkedIds.length === 0}>
              Бараа сонгох
            </Button>
          </Space>
        </Flex>
      }
    >
      <Flex vertical gap={12} style={{ paddingTop: 16 }}>
        <Flex align="center" justify="space-between" gap={16}>
          <Checkbox
            checked={allChecked}
            indeterminate={checkedIds.length > 0 && !allChecked}
            disabled={selectableRows.length === 0}
            onChange={(e) => onCheckAll(e.target.checked)}
          >
            Бүгдийг сонгох
          </Checkbox>
          {rows.length > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {checkedIds.length} / {selectableRows.length} сонгогдсон
            </Text>
          )}
        </Flex>
        <Divider style={{ margin: 0 }} />
      </Flex>

      <div style={{ width: '100%', maxHeight: 440, overflow: 'auto' }}>{renderBody()}</div>
    </Modal>
  );
};

export default ModalSelectProduct;
